import { defineStore } from 'pinia';
import { ref, computed, readonly } from 'vue';
import { branchApi } from '@/entities/Branch';
import type { Branch, BranchFormData } from '@/entities/Branch';

export const useBranchStore = defineStore('branch-management', () => {
  // ===== STATE =====
  const branches = ref<Branch[]>([]);
  const currentBranch = ref<Branch | null>(null);
  const isLoading = ref(false);
  const isCreating = ref(false);
  const isUpdating = ref(false);
  const isDeleting = ref(false);
  const error = ref<string | null>(null);

  // ===== GETTERS =====
  const branchCount = computed(() => branches.value.length);
  const hasBranches = computed(() => branches.value.length > 0);
  const matrizBranch = computed(() => branches.value.find((b) => b.isMain || b.name === 'Matriz'));

  // ===== ACTIONS =====
  async function fetchBranchById(branchId: number | string): Promise<Branch | null> {
    try {
      isLoading.value = true;
      error.value = null;
      const branch = await branchApi.getBranchById(branchId);
      currentBranch.value = branch;
      return branch;
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch branch';
      console.error('Error fetching branch:', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  async function createBranch(formData: FormData): Promise<boolean> {
    try {
      isCreating.value = true;
      error.value = null;
      await branchApi.createBranch(formData);
      return true;
    } catch (err: any) {
      error.value = err.message || 'Failed to create branch';
      console.error('Error creating branch:', err);
      return false;
    } finally {
      isCreating.value = false;
    }
  }

  async function updateBranch(branchId: number | string, formData: FormData): Promise<boolean> {
    try {
      isUpdating.value = true;
      error.value = null;
      await branchApi.updateBranch(branchId, formData);
      return true;
    } catch (err: any) {
      error.value = err.message || 'Failed to update branch';
      console.error('Error updating branch:', err);
      return false;
    } finally {
      isUpdating.value = false;
    }
  }

  async function deleteBranch(branchId: number): Promise<boolean> {
    try {
      isDeleting.value = true;
      error.value = null;
      await branchApi.deleteBranch(branchId);
      // Remove from local state
      branches.value = branches.value.filter((b) => b.id !== branchId);
      return true;
    } catch (err: any) {
      error.value = err.message || 'Failed to delete branch';
      console.error('Error deleting branch:', err);
      return false;
    } finally {
      isDeleting.value = false;
    }
  }

  function setBranches(newBranches: Branch[]) {
    branches.value = newBranches;
  }

  function clearError() {
    error.value = null;
  }

  function clearCurrentBranch() {
    currentBranch.value = null;
  }

  function reset() {
    branches.value = [];
    currentBranch.value = null;
    isLoading.value = false;
    isCreating.value = false;
    isUpdating.value = false;
    isDeleting.value = false;
    error.value = null;
  }

  // ===== RETURN =====
  return {
    // State (readonly for external access)
    branches: readonly(branches),
    currentBranch: readonly(currentBranch),
    isLoading: readonly(isLoading),
    isCreating: readonly(isCreating),
    isUpdating: readonly(isUpdating),
    isDeleting: readonly(isDeleting),
    error: readonly(error),
    // Getters
    branchCount,
    hasBranches,
    matrizBranch,
    // Actions
    fetchBranchById,
    createBranch,
    updateBranch,
    deleteBranch,
    setBranches,
    clearError,
    clearCurrentBranch,
    reset,
  };
});
